<style lang="scss">
.main-menu {
  .brand-logo {
    display: none;
  }

  &.expanded {
    .brand-logo {
      display: block;
    }
  }

  .new-portal-link {
    position: absolute;
    bottom: 0;
    left: 0;
  }
}
</style>

<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light'
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">

          <!-- Logo & Text -->
          <li class="nav-item mr-auto d-flex">
            <b-link
              class="navbar-brand m-0"
              to="/"
            >
              <span class="brand-logo">
                <b-img width="100px"
                       :src="theme === 'light' ? logo : logoWhite"
                       alt="logo"
                />
              </span>
            </b-link>
          </li>

          <!-- Toggler Button -->
          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div
      :class="{'d-block': shallShadowBottom}"
      class="shadow-bottom"
    />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
    >
      <vertical-nav-menu-items
        :items="filteredNavMenuItems"
        class="navigation navigation-main"
      />
      <b-btn v-if="prison && prison.show_new_webportal_button"
             class="new-portal-link" @click="switchToOldSite()" variant="link">Back to old design</b-btn>
    </vue-perfect-scrollbar>
    <!-- /main menu content-->
  </div>
</template>

<script>
import {REST_ENDPOINT} from '../../../../../../endpoints';
import {mapState} from 'vuex';
import navMenuItems from '@/navigation/vertical'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import {BLink, BImg} from 'bootstrap-vue'
import {provide, computed, ref} from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import {$themeConfig} from '@themeConfig'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import useVerticalNavMenu from './useVerticalNavMenu'
import HelperService from '@/services/HelperService';

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  computed: {
    ...mapState({
      theme: state => state.appConfig.layout.skin,
      prison: state => state.prison.prison,
      permissions: state => state.permissions,
    }),
    filteredNavMenuItems() {
      // Define a function to filter individual navigation items based on permissions
      const filterItem = item => {
        // If item does not require any permission, it is visible
        if (!item.requiresPermission) {
          return true;
        }
        // If requiresPermission is an array, all permissions in the array must be met
        if (Array.isArray(item.requiresPermission)) {
          return item.requiresPermission.every(permission => this.permissions[permission]);
        }
        // If requiresPermission is a single permission, check if it is met
        return this.permissions[item.requiresPermission];
      };

      // Define a function to filter children of a navigation item based on permissions
      const filterChildren = children => children.filter(child => {
          // If child item does not require any permission, it is visible
          if (!child.requiresPermission) {
            return true;
          }
          // If requiresPermission is an array, all permissions in the array must be met
          if (Array.isArray(child.requiresPermission)) {
            return child.requiresPermission.every(permission => this.permissions[permission]);
          }
          // If requiresPermission is a single permission, check if it is met
          return this.permissions[child.requiresPermission];
        });

      // Map through each navigation item
      return navMenuItems.map(item => {
        const newItem = { ...item }; // Clone the item to avoid modifying the original data
        // If item has children, filter the children based on permissions
        if (newItem.children) {
          newItem.children = filterChildren(newItem.children);
          // If all children are hidden due to permissions, hide the parent item as well
          if (newItem.children.length === 0) {
            return null;
          }
        }
        // Filter the item itself based on permissions
        return newItem;
      }).filter(item => item && filterItem(item)); // Filter out null items (parent items with all hidden children) and filter items based on permissions
    },
  },
  data() {
    return {
      // eslint-disable-next-line global-require
      logo: require('@/assets/images/logo/logo.svg'),
      logoWhite: require('@/assets/images/logo/logo-white.svg'),
    }
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    provide('isMouseHovered', isMouseHovered)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'))

    // App Name
    const { appName, appLogoImage } = $themeConfig.app

    return {
      navMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,
    }
  },
  mounted() {
    this.getPrison();
  },
  methods: {
    async getPrison() {
      await this.$store.dispatch('prison/getPrison');
    },
    switchToOldSite() {
      const helper = new HelperService();
      helper.switchToOldSite();
    },
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";
</style>
